/**
 * How a vendor's travel radius is read and labelled in the console.
 *
 * Shared by the vendor detail page's Location card and its per-module rows, and
 * kept out of the page because the labelling — not the layout — is what was
 * wrong: see `radius_rows` below.
 */
import { VENDOR_MODULE_TYPE_LABELS, type VendorModuleDetails, type VendorModuleType } from './types';

/** What discovery falls back to when a vendor never declared a radius. */
export const DEFAULT_TRAVEL_RADIUS_KM = 10;

/**
 * A radius enum as a person reads it.
 *
 * `humanize('KM_50')` gives "Km 50", which is why this looked like an unrelated
 * field when a mismatch was being investigated. An undeclared radius is not
 * blank either — discovery falls back to a platform default, so say which,
 * rather than leaving an admin to guess what an em-dash means.
 */
export function radius(
  value: string | null | undefined,
  opts: { filters_discovery?: boolean } = {},
): string {
  if (!value) return `Not set — defaults to ${DEFAULT_TRAVEL_RADIUS_KM} km`;
  const km = value.replace('KM_', '').replace('_PLUS', '+');
  return `${km} km${opts.filters_discovery === false ? ' (not used to filter search)' : ''}`;
}

/**
 * Which modules declare a travel radius, where they keep it, and whether
 * discovery actually narrows on it.
 *
 * Mirrors the backend's service-area specs. GROOMER is at-salon in v1 and
 * RESTAURANT is ranked nearest-first as fixed premises, so both record a radius
 * that no query filters on — worth saying out loud, because the alternative is
 * an operator trusting a number that decides nothing.
 */
export const RADIUS_MODULES: Array<{
  module: string;
  field: string;
  label: string;
  filters_discovery: boolean;
}> = [
  { module: 'WALKER', field: 'service_radius', label: 'Service radius', filters_discovery: true },
  { module: 'TRAINER', field: 'service_radius', label: 'Service radius', filters_discovery: true },
  { module: 'GROOMER', field: 'service_radius', label: 'Service radius', filters_discovery: false },
  { module: 'CREMATION', field: 'service_radius', label: 'Service radius', filters_discovery: true },
  { module: 'LAB', field: 'service_radius', label: 'Service radius', filters_discovery: true },
  {
    module: 'RESTAURANT',
    field: 'delivery_radius',
    label: 'Delivery radius',
    filters_discovery: false,
  },
];

/**
 * The radius rows for the Location card — one per module that has one.
 *
 * `VendorProfile.delivery_radius_km` used to be the single row here, and it was
 * wrong three ways at once: a Decimal defaulting to 5 that no query reads, so a
 * walker who had declared 10km was shown "Delivery radius — 5 km" under a card
 * headed "What nearby search matches against". The number discovery does match
 * on lives on the module profile, and a vendor can run more than one module, so
 * name the module whenever there is more than one radius to tell apart.
 */
export function radius_rows(
  module_details: VendorModuleDetails | undefined,
): Array<{ label: string; value: string }> {
  const details = (module_details ?? {}) as Record<string, Record<string, unknown> | undefined>;
  const present = RADIUS_MODULES.filter((r) => details[r.module]);
  return present.map((r) => ({
    label:
      present.length > 1
        ? `${r.label} · ${VENDOR_MODULE_TYPE_LABELS[r.module as VendorModuleType] ?? r.module}`
        : r.label,
    value: radius(details[r.module]?.[r.field] as string | null | undefined, {
      filters_discovery: r.filters_discovery,
    }),
  }));
}
